import {useIsFeatureStateEnabled} from '../hooks/use-feature-state'
import {ExpandButton} from '@github-ui/expand-button'
// eslint-disable-next-line no-restricted-imports
import {getCurrentSize, ScreenSize} from '@github-ui/screen-size'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useCallback, useMemo, useRef, useState} from 'react'

import {
  type FileTreePreferenceUser,
  updateFileTreePreference,
} from '@github-ui/commits/shared/useUpdateUserTreePreferences'
import styles from '../components/DiffComparisonViewer.module.css'
import {FileTreeToggleButton} from '../components/file-tree/FileTreeToggleButton'
import {measureIsWideViewport} from '../helpers/measure-viewport'

type ExpandTreeFunction = () => void
type CollapseTreeFunction = () => void

type WideMode = 'expanded' | 'collapsed'
type CompactMode = 'overlay' | 'inline' | 'none'

// Consolidated view state for different viewport sizes
type TreeViewState = {
  // Tree display mode for wide viewports (>= 1012px container width)
  wideMode: WideMode
  // Tree display mode for compact viewports (< 1012px container width)
  compactMode: CompactMode
}

interface TreePane {
  // SSR flag for hiding pane during initial render
  splitPagePaneHidden: boolean
  treeToggleElement: React.ReactElement
  collapseMobileTree: CollapseTreeFunction
  expandTree: ExpandTreeFunction
  treeViewState: TreeViewState
}

export function useTreePane(
  fileTreeId: string,
  userPrefTreeExpanded: boolean,
  treeEnableInlineCompactMode: boolean,
  currentUser?: FileTreePreferenceUser,
): TreePane {
  const {isServer: isSSR} = useRuntimeEnvironment()

  const treeToggleRef = useRef<HTMLButtonElement>(null)
  const mobileTreeToggleRef = useRef<HTMLButtonElement>(null)

  // Internal state consolidated into a single object
  const [treeViewState, setTreeViewState] = useState<TreeViewState>({
    wideMode: userPrefTreeExpanded ? 'expanded' : 'collapsed',
    compactMode: 'none',
  })

  const expandRegularTree: ExpandTreeFunction = useCallback(() => {
    setTreeViewState(prev => ({...prev, wideMode: 'expanded'}))
    updateFileTreePreference(true, currentUser)
    requestAnimationFrame(() => treeToggleRef.current?.focus())
  }, [currentUser])

  const collapseRegularTree: CollapseTreeFunction = useCallback(() => {
    setTreeViewState({wideMode: 'collapsed', compactMode: 'none'})
    updateFileTreePreference(false, currentUser)
    requestAnimationFrame(() => treeToggleRef.current?.focus())
  }, [currentUser])

  const expandMobileTree: ExpandTreeFunction = useCallback(() => {
    let mode: CompactMode = 'overlay' // default to overlay

    // Only use inline mode if enabled via config
    if (treeEnableInlineCompactMode) {
      // Determine mode based on screen/viewport size
      // since we already use a container query for the mobile button visibility
      // - Screen ≤768px (large): overlay dialog (ScreenSize.large is 768; screens exactly at 768px use overlay)
      // - Screen >768px: inline fullscreen
      const viewportWidth = window.innerWidth
      const normalizedScreenSize = getCurrentSize(viewportWidth)

      // If screen is large or wider (>768px), use inline mode
      if (normalizedScreenSize > ScreenSize.large) {
        mode = 'inline'
      }
    }

    setTreeViewState(prev => ({...prev, compactMode: mode}))
    requestAnimationFrame(() => mobileTreeToggleRef.current?.focus())
  }, [treeEnableInlineCompactMode])

  const collapseMobileTree: CollapseTreeFunction = useCallback(() => {
    setTreeViewState(prev => ({...prev, compactMode: 'none'}))
    requestAnimationFrame(() => mobileTreeToggleRef.current?.focus())
  }, [])

  const expandTree: ExpandTreeFunction = useCallback(() => {
    if (measureIsWideViewport()) {
      // On wide containers (>= 1012px), use regular tree
      if (treeViewState.wideMode === 'collapsed') {
        expandRegularTree()
      }
    } else {
      // On narrow containers (< 1012px), use mobile tree
      if (treeViewState.compactMode === 'none') {
        expandMobileTree()
      }
    }
  }, [treeViewState.wideMode, treeViewState.compactMode, expandRegularTree, expandMobileTree])

  const hasUpdatedHeader = useIsFeatureStateEnabled('overview')
  const treeLabel = useCallback(
    (expanded: boolean) =>
      expanded
        ? `${hasUpdatedHeader ? 'Hide' : 'Collapse'} file tree`
        : `${hasUpdatedHeader ? 'Show' : 'Expand'} file tree`,
    [hasUpdatedHeader],
  )

  const regularTreeToggleElement = useMemo(
    () =>
      hasUpdatedHeader ? (
        <FileTreeToggleButton
          expanded={treeViewState.wideMode === 'expanded'}
          ariaLabel={treeLabel(treeViewState.wideMode === 'expanded')}
          tooltipDirection="se"
          ariaControls={fileTreeId}
          ref={treeToggleRef}
          className={styles['RegularTreeToggle']}
          onToggleExpanded={() => {
            if (treeViewState.wideMode === 'expanded') {
              collapseRegularTree()
            } else {
              expandRegularTree()
            }
          }}
          size="small"
        />
      ) : (
        <ExpandButton
          expanded={treeViewState.wideMode === 'expanded'}
          alignment="left"
          ariaLabel={treeLabel(treeViewState.wideMode === 'expanded')}
          tooltipDirection="se"
          testid="file-tree-button"
          ariaControls={fileTreeId}
          ref={treeToggleRef}
          className={styles['RegularTreeToggle']}
          onToggleExpanded={() => {
            if (treeViewState.wideMode === 'expanded') {
              collapseRegularTree()
            } else {
              expandRegularTree()
            }
          }}
          size="small"
        />
      ),
    [treeViewState.wideMode, fileTreeId, collapseRegularTree, expandRegularTree, treeLabel, hasUpdatedHeader],
  )

  const mobileTreeToggleElement = useMemo(
    () =>
      hasUpdatedHeader ? (
        <FileTreeToggleButton
          expanded={treeViewState.compactMode !== 'none'}
          ariaLabel={treeLabel(treeViewState.compactMode !== 'none')}
          tooltipDirection="se"
          ariaControls={fileTreeId}
          ref={mobileTreeToggleRef}
          className={styles['MobileTreeToggle']}
          onToggleExpanded={() => {
            if (treeViewState.compactMode !== 'none') {
              collapseMobileTree()
            } else {
              expandMobileTree()
            }
          }}
          size="small"
        />
      ) : (
        <ExpandButton
          expanded={treeViewState.compactMode !== 'none'}
          alignment="left"
          ariaLabel={treeLabel(treeViewState.compactMode !== 'none')}
          tooltipDirection="se"
          testid="file-tree-button"
          ariaControls={fileTreeId}
          ref={mobileTreeToggleRef}
          className={styles['MobileTreeToggle']}
          onToggleExpanded={() => {
            if (treeViewState.compactMode !== 'none') {
              collapseMobileTree()
            } else {
              expandMobileTree()
            }
          }}
          size="small"
        />
      ),
    [treeViewState.compactMode, fileTreeId, collapseMobileTree, expandMobileTree, treeLabel, hasUpdatedHeader],
  )

  const treeToggleElement = useMemo(
    () => (
      <>
        {mobileTreeToggleElement}
        {regularTreeToggleElement}
      </>
    ),
    [mobileTreeToggleElement, regularTreeToggleElement],
  )

  return {
    splitPagePaneHidden: isSSR && !userPrefTreeExpanded,
    treeViewState,
    collapseMobileTree,
    expandTree,
    treeToggleElement,
  }
}
